import React, { useState, useEffect } from 'react';
import useCourseCategories from 'PlatForms/SelfLMS/Hooks/Courses/useCourseCategories';
import './PersonalInterest.css';

const PersonalInterest = () => {
    const {
        mainCategories,
        loading,
        loadingMore,
        error,
        selectedCategories,
        isSubmitting,
        submitError,
        submitSuccess,
        interestLoading,
        pagination,
        hasMoreCategories,
        fetchMoreCategories,
        toggleCategorySelection,
        clearSelections,
        submitSelections,
        refetch
    } = useCourseCategories();
    
    console.log(" ......... main categories", mainCategories);

    // Local state for expanded domains
    const [expandedDomains, setExpandedDomains] = useState({});

    // Domain icons mapping
    const domainIcons = {
        'Software Creation': 'bi-code-slash',
        'Data & Intelligence': 'bi-graph-up',
        'Infrastructure & Security': 'bi-shield-check',
        'Strategic Leadership': 'bi-diagram-3',
        'Operational Excellence': 'bi-speedometer2',
        'Business Analysis': 'bi-clipboard-data',
    };

    // Default icon for categories without specific mapping
    const getDomainIcon = (categoryName) => {
        return domainIcons[categoryName] || 'bi-folder';
    };

    // Get comma-separated list of subcategory names (limited to 3 for display)
    const getSubcategoryNames = (mainCategory) => {
        if (!mainCategory.subCategories || mainCategory.subCategories.length === 0) {
            return mainCategory.description || 'Explore various focus areas in this domain';
        }

        const subcategoryNames = mainCategory.subCategories.map(sub => sub.name);
        if (subcategoryNames.length <= 3) {
            return subcategoryNames.join(', ');
        } else {
            return `${subcategoryNames.slice(0, 3).join(', ')} and ${subcategoryNames.length - 3} more...`;
        }
    };

    // Toggle domain expansion
    const toggleDomain = (domainId) => {
        setExpandedDomains(prev => ({
            ...prev,
            [domainId]: !prev[domainId]
        }));
    };

    // Initialize expanded domains when data is loaded
    useEffect(() => {
        if (mainCategories.length > 0 && !loading && !interestLoading) {
            const expanded = {};

            // If there are selected categories, expand domains that contain them
            if (selectedCategories.length > 0) {
                selectedCategories.forEach(selectedCat => {
                    const parentCategory = mainCategories.find(mc =>
                        mc.subCategories && mc.subCategories.some(sub => sub.id === selectedCat.id)
                    );
                    if (parentCategory) {
                        expanded[parentCategory.id] = true;
                    }
                });
            } else {
                // If no selections, expand the first domain
                expanded[mainCategories[0].id] = true;
            }

            setExpandedDomains(expanded);
        }
    }, [mainCategories, selectedCategories, loading, interestLoading]);

    // Event handlers
    const handleFocusAreaClick = (category) => {
        toggleCategorySelection(category);
    };

    const handleRemoveSelection = (categoryId) => {
        const categoryToRemove = selectedCategories.find(cat => cat.id === categoryId);
        if (categoryToRemove) {
            toggleCategorySelection(categoryToRemove);
        }
    };

    // Progress bar logic with validation rules
    const selectedCount = selectedCategories.length;
    const minSelections = 3;     // Minimum required to submit
    const maxProgressSelections = 7;  // Goal for full progress bar
    const maxAllowedSelections = 11;  // Maximum allowed selections

    let progressPercent;
    let progressText;
    let selectionStatus;
    const isReadyToSubmit = selectedCount >= minSelections;
    const isAtMaxLimit = selectedCount >= maxAllowedSelections;

    if (selectedCount === 0) {
        progressPercent = 0;
        progressText = `Select at least ${minSelections} focus areas to begin`;
        selectionStatus = 'none';
    } else if (selectedCount < minSelections) {
        progressPercent = (selectedCount / minSelections) * 50; // 50% of progress bar
        progressText = `Select ${minSelections - selectedCount} more to proceed (Minimum: ${minSelections})`;
        selectionStatus = 'below-minimum';
    } else if (selectedCount >= minSelections && selectedCount < maxProgressSelections) {
        const progressRange = maxProgressSelections - minSelections;
        const currentProgress = selectedCount - minSelections;
        progressPercent = 50 + (currentProgress / progressRange) * 50; // 50% to 100%
        progressText = `Great! Keep going to reach the goal of ${maxProgressSelections} areas`;
        selectionStatus = 'good';
    } else if (selectedCount >= maxProgressSelections && selectedCount < maxAllowedSelections) {
        progressPercent = 100; // Progress bar is full at 7 selections
        progressText = `Excellent! You've reached the goal of ${maxProgressSelections} areas. You can select up to ${maxAllowedSelections} total.`;
        selectionStatus = 'excellent';
    } else {
        progressPercent = 100; // Progress bar remains full
        progressText = `Maximum ${maxAllowedSelections} areas selected`;
        selectionStatus = 'maximum';
    }

    // Handle submit
    const handleSubmit = async () => {
        if (!isReadyToSubmit) {
            alert(`Please select a minimum of ${minSelections} focus areas to build your learning path.`);
            return;
        }

        // Clear previous messages
        if (submitError) {
            // Clear error if it exists
        }

        const result = await submitSelections();

        if (result && result.success) {
            // Success is handled by the hook state
            console.log('Submission successful:', result);
        } else if (result && !result.success) {
            // Error is already set in hook state
            console.log('Submission failed:', result.error);
        }
    };

    // Show success message
    useEffect(() => {
        if (submitSuccess) {
            const timer = setTimeout(() => {
                // You could clear the success message after some time
                // setSubmitSuccess(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [submitSuccess]);

    // Combined loading state
    const isLoading = loading || interestLoading;

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading your interests and categories...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="alert alert-danger">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <strong>Error:</strong> {error}
                </div>
                <button className="btn btn-primary mt-3" onClick={refetch}>
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Try Again
                </button>
            </div>
        );
    }

    // Determine if we're in edit mode (has existing selections)
    const isEditMode = selectedCategories.length > 0;

    return (
        <div className="main-container">
            {/* Header */}
            <div className="page-header">
                <h1 className="page-title">
                    {isEditMode ? 'Update Your Learning Journey' : 'Shape Your Learning Journey'}
                </h1>
                <p className="page-subtitle">
                    {isEditMode
                        ? 'Update your focus areas to refine your personalized learning path.'
                        : 'Select your focus areas across different domains. We will create a personalized learning path based on your interests and goals.'
                    }
                </p>
                <div className="mt-4">
                    <span className="badge bg-light text-primary p-2 me-2">
                        <i className="bi bi-check-circle me-1"></i> Minimum {minSelections} to proceed
                    </span>
                    <span className="badge bg-light text-primary p-2 me-2">
                        <i className="bi bi-trophy me-1"></i> Goal {maxProgressSelections} for full progress
                    </span>
                    <span className="badge bg-light text-primary p-2">
                        <i className="bi bi-star me-1"></i> Maximum {maxAllowedSelections} allowed
                    </span>
                </div>
            </div>

            {/* Success Message */}
            {submitSuccess && (
                <div className="container mt-3">
                    <div className="alert alert-success alert-dismissible fade show" role="alert">
                        <i className="bi bi-check-circle-fill me-2"></i>
                        <strong>Success!</strong> Your learning interests have been {isEditMode ? 'updated' : 'saved'} successfully.
                        You selected {selectedCount} focus areas.
                        <button type="button" className="btn-close" onClick={() => {/* Clear success */ }}></button>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {submitError && (
                <div className="container mt-3">
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        <strong>Error:</strong> {submitError}
                        <button type="button" className="btn-close" onClick={() => {/* Clear error */ }}></button>
                    </div>
                </div>
            )}

            <div className="container">
                <div className="row">
                    {/* Main Content - Domains & Specializations */}
                    <div className="col-lg-8">
                        {mainCategories.length === 0 ? (
                            <div className="alert alert-info">
                                <i className="bi bi-info-circle me-2"></i>
                                No categories with sub-categories found.
                            </div>
                        ) : (
                            <>
                                {mainCategories.map((mainCategory) => (
                                    <div className="domain-section" key={mainCategory.id}>
                                        <div
                                            className={`domain-header ${expandedDomains[mainCategory.id] ? 'active' : ''}`}
                                            onClick={() => toggleDomain(mainCategory.id)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="domain-title">
                                                <div className="domain-icon">
                                                    <i className={`bi ${getDomainIcon(mainCategory.name)} fs-4`}></i>
                                                </div>
                                                <div>
                                                    <div>{mainCategory.name}</div>
                                                    <div className="small text-muted mt-1">
                                                        {getSubcategoryNames(mainCategory)}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="domain-indicator">
                                                <i className={`bi bi-chevron-${expandedDomains[mainCategory.id] ? 'up' : 'down'}`}></i>
                                            </div>
                                        </div>

                                        {expandedDomains[mainCategory.id] && mainCategory.subCategories && mainCategory.subCategories.length > 0 && (
                                            <div className="specializations-container show">
                                                <div className="specialization-group">
                                                    <div className="group-title">{mainCategory.name} Specializations</div>
                                                    <div>
                                                        {mainCategory.subCategories.map((subCategory) => {
                                                            const isSelected = selectedCategories.some(cat => cat.id === subCategory.id);
                                                            const isDisabled = isAtMaxLimit && !isSelected;

                                                            return (
                                                                <span
                                                                    key={subCategory.id}
                                                                    className={`focus-area ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                                                    onClick={() => !isDisabled && handleFocusAreaClick(subCategory)}
                                                                    style={{
                                                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                                        opacity: isDisabled ? 0.6 : 1
                                                                    }}
                                                                    title={isDisabled ? `Maximum ${maxAllowedSelections} selections reached` : ''}
                                                                >
                                                                    {isSelected && <span className="checkmark"><i className="bi bi-check"></i></span>}
                                                                    {subCategory.name}
                                                                    {isDisabled && <span className="ms-1">(Max reached)</span>}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {/* Show More Button - Only if we have more categories to load */}
                                {hasMoreCategories && (
                                    <div className="load-more-container mt-4 text-center">
                                        <button 
                                            className="btn btn-outline-primary btn-lg"
                                            onClick={fetchMoreCategories}
                                            disabled={loadingMore}
                                            style={{ 
                                                padding: '10px 30px',
                                                fontSize: '1rem'
                                            }}
                                        >
                                            {loadingMore ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Loading...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-arrow-down-circle me-2"></i>
                                                    Show More Domains 
                                                </>
                                            )}
                                        </button>
                                        <p className="text-muted mt-2 small">
                                            Showing {mainCategories.length} of {pagination.totalCount} domains
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Sidebar - Selection Summary & Actions */}
                    <div className="col-lg-4">
                        <div className="selection-sidebar">
                            {/* Progress Tracker */}
                            <div className="selection-card">
                                <div className="selection-title">
                                    <i className="bi bi-graph-up"></i> Your Progress
                                </div>
                                <div className="progress-container">
                                    <div className="progress-label">
                                        <span>Selection Progress</span>
                                        <span id="selectedCount" className={`fw-bold ${selectionStatus === 'maximum' ? 'text-danger' :
                                                selectionStatus === 'excellent' ? 'text-success' :
                                                    selectionStatus === 'good' ? 'text-primary' :
                                                        'text-muted'
                                            }`}>
                                            {selectedCount} selected
                                        </span>
                                    </div>
                                    <div className="progress-bar">
                                        <div
                                            id="progressFill"
                                            className="progress-fill"
                                            style={{
                                                width: `${progressPercent.toFixed(0)}%`,
                                                backgroundColor:
                                                    selectionStatus === 'maximum' ? '#dc3545' :
                                                        selectionStatus === 'excellent' || selectionStatus === 'good' ? '#28a745' :
                                                            '#007bff'
                                            }}
                                        ></div>
                                    </div>
                                    <div className="mt-2 small">
                                        <span id="progressText" className={`
                                            ${selectionStatus === 'maximum' ? 'text-danger' :
                                                selectionStatus === 'excellent' ? 'text-success' :
                                                    selectionStatus === 'good' ? 'text-primary' :
                                                        'text-muted'}
                                        `}>
                                            {progressText}
                                        </span>
                                    </div>
                                    <div className="mt-3">
                                        <div className="d-flex justify-content-between small text-muted">
                                            <span>Minimum: {minSelections}</span>
                                            <span>Goal: {maxProgressSelections}</span>
                                            <span>Maximum: {maxAllowedSelections}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Selected Items */}
                            <div className="selection-card">
                                <div className="selection-title">
                                    <i className="bi bi-bookmark-check"></i> Your Focus Areas
                                    <span id="selectionCount" className={`selection-count ${selectedCount >= maxAllowedSelections ? 'bg-danger' :
                                            selectedCount >= maxProgressSelections ? 'bg-success' :
                                                'bg-accent'
                                        }`}>
                                        {selectedCount}
                                    </span>
                                </div>

                                <div className="selected-items" id="selectedItems">
                                    {selectedCount === 0 ? (
                                        <div className="empty-state" id="emptyState">
                                            <i className="bi bi-inbox"></i>
                                            <p>No focus areas selected yet</p>
                                            <p className="small">Click on any focus area to add it here</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="mb-2 small text-muted">
                                                Selected: {selectedCount}/{maxAllowedSelections}
                                            </div>
                                            {selectedCategories.map((category) => {
                                                // Find the parent category name for display
                                                const parentCategory = mainCategories.find(mc =>
                                                    mc.id === category.parentCategoryId
                                                );
                                                return (
                                                    <div className="selected-item" key={category.id}>
                                                        <div>
                                                            <div className="item-name">{category.name}</div>
                                                            <div className="item-domain">
                                                                {parentCategory?.name || 'General'}
                                                            </div>
                                                        </div>
                                                        <button
                                                            className="remove-btn"
                                                            onClick={() => handleRemoveSelection(category.id)}
                                                            disabled={isSubmitting}
                                                        >
                                                            <i className="bi bi-x-lg"></i>
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div>
                                <button
                                    className={`action-btn primary-action ${!isReadyToSubmit || isSubmitting ? 'disabled' : ''}`}
                                    onClick={handleSubmit}
                                    disabled={!isReadyToSubmit || isSubmitting}
                                    id="submitBtn"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-rocket-takeoff me-2"></i>
                                            {isEditMode ? 'Update My Learning Path' : 'Build My Learning Path'}
                                        </>
                                    )}
                                </button>
                                <button
                                    className="action-btn secondary-action"
                                    onClick={clearSelections}
                                    disabled={isSubmitting}
                                    id="clearBtn"
                                >
                                    <i className="bi bi-arrow-clockwise me-2"></i> Start Over
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PersonalInterest;